'use client';

import { useEffect } from 'react';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Top-level error boundary.
export default function ErrorPage({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    console.error('Unhandled render error:', error);
  }, [error]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-6">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-nocturne" />
      <div className="relative w-full max-w-lg space-y-6 rounded-xl border border-hairline bg-surface/80 p-9 shadow-lg backdrop-blur-sm">
        <div className="space-y-3">
          <p className="eyebrow" style={{ color: 'hsl(var(--priority-urgent))' }}>
            Error
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight">
            Something broke.
          </h2>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {error.message || 'An unknown error occurred. Try reloading.'}
        </p>
        {error.digest && (
          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-subtle-foreground">
            digest · {error.digest}
          </p>
        )}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={reset}
            className="rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition duration-fast ease-out-quint hover:brightness-110"
          >
            Try again
          </button>
          <a
            href="/"
            className="text-sm font-medium text-muted-foreground transition duration-fast ease-out-quint hover:text-foreground"
          >
            Back to the workspace →
          </a>
        </div>
      </div>
    </div>
  );
}
